import {
    type ColumnMovedEvent,
    type ColumnPinnedEvent,
    type ColumnResizedEvent,
    type GridApi,
    type SelectionChangedEvent,
    type SortChangedEvent,
} from "ag-grid-enterprise";
import { useCallback, useEffect, useMemo, useState } from "react";

import { type IBaseEntity } from "types/apiResponse/apiResponse";

import { AgGridReact } from "commonComponents/utilities/AgGridReact/AgGridReact";
import { type IAgGridColumn } from "commonComponents/utilities/AgGridReact/AgGridReact.types";
import { mapBTColToAgCol } from "commonComponents/utilities/AgGridReact/AgGridReact.utility";
import {
    type GridColumn,
    type IGridRowData,
    type IGroupedRowData,
} from "commonComponents/utilities/Grid/GridContainer.types";
import { GridContainerHeader } from "commonComponents/utilities/Grid/GridContainerHeader";

import "./BillListAgGrid.less";

import { getBillAgColumnImplementations } from "./billAgColumnImplementations";
import { BillAgHeader } from "./BillAgHeader";
import { type IBillGridRow } from "./billAgRow.types";

/**
 * Minimal sort shape the grid emits back to BillList. Structurally compatible with the
 * Kendo `SortDescriptor` BillList's server-side handler consumes, without coupling this
 * new AG Grid component to the Kendo data-query package (restricted import).
 */
export interface IBillSortDescriptor {
    field: string;
    dir: "asc" | "desc";
}

/**
 * Scaffolding for the AG Grid rendering of the Bills List (ado-281381), gated behind the
 * `billsAgGridMigration` flag. Mirrors the ScheduleList pattern: a thin presentational
 * component over the shared `AgGridReact` wrapper that maps the existing server-driven
 * `gridState.columnsList` into AG Grid `ColDef`s and drives sort/column changes back through
 * BillList's existing server-side handlers (client-data row model — the current
 * `/api/v1/bills/grid` contract is unchanged).
 *
 * Row selection is owned here (grid ref + selected-rows state) and surfaced to the parent
 * only through `renderToolbarContent`, mirroring GridContainer's `toolbarContentConfig`.
 * Bill-specific column implementations, footer/totals, and the saved-view toolbar are layered
 * on in follow-up work.
 */
interface IBillListAgGridProps {
    data: (IGridRowData | IGroupedRowData)[];
    columnsList: GridColumn<unknown>[];
    /**
     * Per-column footer totals keyed by column jsonKey (e.g. the summed bill amount), as
     * produced by the server and stored in `gridState.footerData`. Rendered as a pinned
     * bottom row. `null`/undefined means no footer.
     */
    footerData?: { [jsonKey: string]: unknown } | null;
    /** Reuses BillList's existing server-side sort handler (SortDescriptor-compatible shape). */
    onSortChanged: (sortingData: IBillSortDescriptor[]) => void | Promise<void>;
    /**
     * The active sort (from the current/applied saved view), keyed by column id. Seeded into the
     * column defs so AG Grid shows the sort indicator and the header toggles from the right state,
     * matching the server-applied order. Empty/undefined means no active sort.
     */
    sortingData?: IBillSortDescriptor[];
    /**
     * Column-layout persistence. This component owns the AG Grid → domain translation: it maps AG
     * column resize/move/pin events into a change and emits an *updater* (matched by column `id`).
     * The updater is applied by the parent against its latest `columnsList` via a functional state
     * update, so two AG events firing before React re-renders (e.g. a drag that both moves and
     * resizes) can't clobber each other by both closing over the same stale render snapshot. No AG
     * Grid types leak to the parent.
     */
    onColumnsListChange?: (
        updater: (current: GridColumn<unknown>[]) => GridColumn<unknown>[],
    ) => void;
    hasSelectableRows: boolean;
    /**
     * Selects standalone-page vs. modal-overlay links for bill/lien-waiver columns.
     * Pass `builderInfo.flags.billsFullPageConversion`, mirroring the Kendo column factory.
     */
    useAbsoluteLink: boolean;
    /**
     * Renders the toolbar content above the grid from the currently selected rows, mirroring
     * GridContainer's `toolbarContentConfig.renderToolbarContent`. Bills does not use the
     * `CheckedActions` (selected count + Cancel) banner — its Kendo callback ignores that
     * second argument too — so it is intentionally not passed here (parity with Kendo Bills).
     */
    renderToolbarContent?: (entities: IBaseEntity[]) => React.ReactNode | void;
    /**
     * Content rendered between the toolbar header and the grid (e.g. the Bills tabs + inbox
     * dropzone bar), mirroring GridContainer's `stationaryHeader` slot so it sits directly above
     * the table rather than above the page's content header.
     */
    stationaryHeader?: React.ReactNode;
    /**
     * Renders the floating action bar for bulk actions when rows are selected, mirroring
     * GridContainer's `renderFab`. Called with a deselect callback, the selected count, and the
     * selected rows; returns null when nothing is selected.
     */
    renderFab?: (
        onDeselect: () => void,
        selectedCount: number,
        selectedEntities: IBaseEntity[],
    ) => React.ReactNode;
    /**
     * Empty-state UI shown in place of the grid when there are no rows (filtered to zero or no
     * bills). Mirrors GridContainer's `gridEmptyState`. `null` means the grid has data.
     */
    emptyState?: React.ReactNode;
    /**
     * Shows AG Grid's loading overlay while a server-side reload is in flight (sort, paging,
     * filters, view change), so a header-sort click gives immediate feedback before data updates.
     */
    loading?: boolean;
}

export const BILLS_AG_GRID_ID = "billsList";

// AG Grid's internal id for the auto-generated selection (checkbox) column. It's excluded from the
// persisted column order. Kept as a named constant so an AG Grid upgrade that renames it surfaces
// here in one place rather than silently folding the selection column into users' saved reorders.
const AG_SELECTION_COLUMN_ID = "ag-Grid-SelectionColumn";

export const BillListAgGrid = (props: IBillListAgGridProps) => {
    // Hold the grid API in state (set on grid-ready) rather than reading a ref during render — the
    // FAB's deselect callback is passed to renderFab in the render body, and reading gridRef.current
    // there trips react-hooks/refs. AG Grid also hands the api to each event, used directly below.
    const [gridApi, setGridApi] = useState<GridApi<IBillGridRow> | null>(null);
    const [selectedRows, setSelectedRows] = useState<IBaseEntity[]>([]);

    const columnDefs = useMemo(() => {
        // Seed the active sort (keyed by column id) so AG Grid renders the sort indicator and the
        // header toggles from the applied direction instead of restarting at ascending.
        const sortById = new Map(
            (props.sortingData ?? []).map((s, index) => [s.field, { dir: s.dir, index }]),
        );
        return props.columnsList.map((col) => {
            const colId = col.id != null ? String(col.id) : undefined;
            const activeSort = colId != null ? sortById.get(colId) : undefined;
            return {
                ...mapBTColToAgCol(col as unknown as IAgGridColumn),
                // AG Grid reports colId in resize/move/pin events. Bills' column `id` (e.g. "6")
                // differs from `jsonKey` (e.g. "jobName"), and mapBTColToAgCol leaves colId
                // defaulting to field=jsonKey — so pin colId to the integer id here to match
                // columnsList entries by id. (Not in mapBTColToAgCol; other grids rely on
                // colId = field.)
                colId,
                // Render persisted freeze state. Kendo's isFrozen is a boolean (freeze to the
                // left), so map it to AG Grid's "left" pin; the pushpin header toggles it. Use
                // `null` (not `undefined`) when unfrozen: on a columnDefs update AG Grid treats
                // `undefined` as "leave the current pin as-is", so switching to a view without
                // this column pinned would leave it stuck pinned. `null` unpins explicitly.
                pinned: col.isFrozen ? ("left" as const) : null,
                // Use initial* (uncontrolled) so AG Grid shows the applied view's sort but still
                // owns sort changes from header clicks. Using controlled `sort` here fights the
                // click: each re-render re-seeds the old sort and the user's click never sticks.
                initialSort: activeSort?.dir,
                initialSortIndex: activeSort?.index,
                // Approvers renders a wrapping list of badges; let the row grow to fit them all
                // instead of clipping to the fixed row height.
                autoHeight: col.jsonKey === "approvers",
            };
        });
    }, [props.columnsList, props.sortingData]);

    const columnTypes = useMemo(
        () => getBillAgColumnImplementations(props.useAbsoluteLink),
        [props.useAbsoluteLink],
    );

    // Render the server-provided totals as a single pinned bottom row. `footerData` is keyed by
    // column jsonKey; AG Grid exposes each footer value on the pinned row as `params.value` for the
    // matching column. Currently only BillAmountCell renders a footer (it checks
    // `params.node.rowPinned === "bottom"` and reads `params.value`); other renderers read
    // `params.data` and render nothing on the footer row. It's a totals pseudo-row, not a real grid
    // row, so we cast it to the grid's row type for AG Grid's prop.
    const pinnedBottomRowData = useMemo(
        () => (props.footerData ? [props.footerData as unknown as IBillGridRow] : undefined),
        [props.footerData],
    );

    // AG Grid emits a SortChangedEvent; translate the primary sorted column back into the
    // SortDescriptor shape BillList already sends to the server. colId defaults to the
    // column's jsonKey (mapBTColToAgCol sets `field`), which is what mapGridStateToRequest
    // uses as sortColumn.
    const handleSortChanged = (event: SortChangedEvent) => {
        const sortedColumn = event.columns?.find((c) => c.getSort() != null);
        const sortingData: IBillSortDescriptor[] = sortedColumn
            ? [{ field: sortedColumn.getColId(), dir: sortedColumn.getSort() ?? "asc" }]
            : [];
        void props.onSortChanged(sortingData);
    };

    const handleSelectionChanged = (event: SelectionChangedEvent<IBillGridRow>) => {
        setSelectedRows((event.api.getSelectedRows() as IBaseEntity[]) ?? []);
    };

    // Clear selection whenever the row data identity changes (tab switch, filter, sort, paging, view
    // reload). The grid stays mounted across these server reloads and no `getRowId` is supplied, so
    // AG Grid does not guarantee a selectionChanged event on a rowData swap — without this, the FAB
    // and toolbar (fed by `selectedRows`) could keep reporting bills no longer in the grid and drive
    // bulk Approve/Void/Send-to-Accounting against stale rows. Reset both React state and AG Grid's
    // internal selection so they can't diverge.
    useEffect(() => {
        setSelectedRows([]);
        gridApi?.deselectAll();
    }, [props.data, gridApi]);

    // Translate AG Grid column events into an updated columnsList (matched by column id, which we
    // pinned as colId above) and emit the plain domain array. AG fires resize/move continuously
    // during a drag, so act only on drag-end (`event.finished`).
    const handleColumnResized = (event: ColumnResizedEvent) => {
        if (!event.finished || !event.column) {
            return;
        }
        const colId = event.column.getColId();
        const width = event.column.getActualWidth();
        // Emit an updater applied against the parent's latest columnsList (not this render's
        // props.columnsList) so a concurrent move/pin event can't overwrite this resize.
        // getColId() is always a string; normalize col.id (which may be numeric at runtime despite
        // its string type) with String() so the match can't silently miss and drop the resize.
        props.onColumnsListChange?.((current) =>
            current.map((col) => (String(col.id) === colId ? { ...col, width } : col)),
        );
    };

    const handleColumnMoved = (event: ColumnMovedEvent) => {
        if (!event.finished) {
            return;
        }
        const orderedIds = event.api
            .getColumnState()
            .map((col) => col.colId)
            .filter((colId) => colId !== AG_SELECTION_COLUMN_ID);
        const orderIndexById = new Map(orderedIds.map((id, index) => [id, index]));
        // Emit an updater applied against the parent's latest columnsList (not this render's
        // props.columnsList) so a concurrent resize/pin event can't overwrite this reorder.
        // Reorder by AG Grid's reported order, then append any columnsList entry AG didn't report
        // (keeping its relative order). Merging rather than rebuilding from getColumnState alone
        // ensures a reorder can never silently drop a column from the persisted view.
        props.onColumnsListChange?.((current) => {
            const reordered = current
                .map((col, originalIndex) => ({
                    col,
                    // String(col.id): getColumnState colIds are strings; guard against a numeric id.
                    rank: orderIndexById.get(String(col.id)) ?? orderedIds.length + originalIndex,
                }))
                .sort((a, b) => a.rank - b.rank)
                .map(({ col }, index) => ({ ...col, order: index }));
            // No reportable columns (e.g. empty grid) — leave state untouched.
            return reordered.length > 0 ? reordered : current;
        });
    };

    const handleColumnPinned = (event: ColumnPinnedEvent) => {
        // Bills' persisted model is a boolean (isFrozen) that renders as a left pin, so any pin
        // (left or right) is stored as frozen; a right pin therefore reloads as a left pin.
        const changed = new Map(
            (event.columns ?? []).map((c) => [c.getColId(), c.getPinned() != null]),
        );
        // Emit an updater applied against the parent's latest columnsList (not this render's
        // props.columnsList) so a concurrent resize/move event can't overwrite this pin change.
        // Only emit if a frozen state actually changed, so programmatic/initial pin events don't
        // falsely flag the view as having unsaved changes.
        props.onColumnsListChange?.((current) => {
            let didChange = false;
            const updated = current.map((col) => {
                // String(col.id): getColId() returns strings; guard against a numeric id.
                const id = String(col.id);
                if (!changed.has(id) || col.isFrozen === changed.get(id)) {
                    return col;
                }
                didChange = true;
                return { ...col, isFrozen: changed.get(id)! };
            });
            return didChange ? updated : current;
        });
    };

    const toolbarContent = props.renderToolbarContent?.(selectedRows);

    const handleDeselectAll = useCallback(() => {
        gridApi?.deselectAll();
        setSelectedRows([]);
    }, [gridApi]);

    const fab = props.renderFab?.(handleDeselectAll, selectedRows.length, selectedRows);

    // Render the empty state in place of the grid when there are no rows, mirroring GridContainer.
    // While a reload is in flight, keep the grid mounted so its loading overlay shows instead.
    const isEmpty = props.emptyState != null && props.data.length === 0 && !props.loading;

    return (
        <div className="BillListAgGridWrapper">
            {toolbarContent && <GridContainerHeader content={toolbarContent} autoHeight />}
            {props.stationaryHeader}
            {isEmpty ? (
                props.emptyState
            ) : (
                <AgGridReact<IBillGridRow>
                    gridId={BILLS_AG_GRID_ID}
                    onGridReady={(event) => setGridApi(event.api)}
                    // domLayout="normal" keeps AG Grid virtualized (only visible rows in the DOM).
                    // The wrapper (.less) gives the grid a viewport-relative height, so it stays
                    // bounded without ScheduleList's JS height workaround.
                    domLayout="normal"
                    columnDefs={columnDefs}
                    columnTypes={columnTypes}
                    data={props.data as unknown as IBillGridRow[]}
                    loading={props.loading}
                    pinnedBottomRowData={pinnedBottomRowData}
                    defaultColDef={{
                        sortable: true,
                        editable: false,
                        resizable: true,
                        // Custom header (BillAgHeader): click the label to sort, click the pushpin
                        // to freeze. Suppress AG Grid's default three-dot menu so the pushpin is the
                        // only pin affordance (Kendo parity). onColumnPinned persists the freeze.
                        headerComponent: BillAgHeader,
                        suppressHeaderMenuButton: true,
                        suppressHeaderContextMenu: true,
                        minWidth: 120,
                    }}
                    suppressMultiSort={true}
                    onSortChanged={handleSortChanged}
                    onColumnResized={handleColumnResized}
                    onColumnMoved={handleColumnMoved}
                    onColumnPinned={handleColumnPinned}
                    onSelectionChanged={handleSelectionChanged}
                    rowSelection={
                        props.hasSelectableRows
                            ? { mode: "multiRow", hideDisabledCheckboxes: true }
                            : undefined
                    }
                />
            )}
            {fab}
        </div>
    );
};
